import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { voiceCloningAPI } from '../../services/api';
import { 
  Mic, Upload, Play, Pause, Trash2, Plus, Volume2, Loader2, 
  AlertCircle, CheckCircle, X, RefreshCw, Wand2
} from 'lucide-react';

const CloneVoicePage = () => {
  const { clientUser } = useAuth();
  const [voices, setVoices] = useState({ cloned: [], available: [] });
  const [loading, setLoading] = useState(true);
  const [apiConfigured, setApiConfigured] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Clone voice modal state
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneName, setCloneName] = useState('');
  const [cloneDescription, setCloneDescription] = useState('');
  const [audioFiles, setAudioFiles] = useState([]);
  const [cloning, setCloning] = useState(false);
  
  // TTS Preview state
  const [previewVoiceId, setPreviewVoiceId] = useState(null);
  const [previewText, setPreviewText] = useState('Hello, this is a test of the cloned voice.');
  const [generatingTTS, setGeneratingTTS] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchVoices = useCallback(async () => {
    if (!clientUser?.id) return;
    
    setLoading(true);
    try {
      const response = await voiceCloningAPI.getVoices(clientUser.id);
      if (response.data.success) {
        setVoices({
          cloned: response.data.cloned_voices || [],
          available: response.data.available_voices || []
        });
        setApiConfigured(response.data.api_configured);
      }
    } catch (err) {
      console.error('Error fetching voices:', err);
      setError('Failed to load voices');
    } finally {
      setLoading(false);
    }
  }, [clientUser?.id]);

  useEffect(() => {
    fetchVoices();
  }, [fetchVoices]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => 
      ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/x-m4a'].includes(f.type)
    );
    
    if (validFiles.length !== files.length) {
      setError('Some files were skipped. Only MP3, WAV, and M4A are supported.');
      setTimeout(() => setError(null), 3000);
    }
    
    setAudioFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setAudioFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleCloneVoice = async () => {
    if (!cloneName.trim()) {
      setError('Please enter a voice name');
      return;
    }
    if (audioFiles.length === 0) {
      setError('Please upload at least one audio sample');
      return;
    }
    
    setCloning(true);
    setError(null);
    
    try {
      const response = await voiceCloningAPI.cloneVoice(
        clientUser.id,
        cloneName,
        cloneDescription,
        audioFiles
      );
      
      if (response.data.success) {
        setSuccess('Voice cloned successfully!');
        setShowCloneModal(false);
        setCloneName('');
        setCloneDescription('');
        setAudioFiles([]);
        fetchVoices();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to clone voice');
    } finally {
      setCloning(false);
    }
  };

  const handleDeleteVoice = async (voiceId) => {
    if (!window.confirm('Are you sure you want to delete this voice?')) return;
    
    try {
      await voiceCloningAPI.deleteVoice(clientUser.id, voiceId);
      setSuccess('Voice deleted successfully');
      fetchVoices();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete voice');
    }
  };

  const handlePreviewVoice = async (voiceId) => {
    setPreviewVoiceId(voiceId);
    setGeneratingTTS(true);
    setAudioUrl(null);
    
    try {
      const response = await voiceCloningAPI.generateTTS(
        clientUser.id,
        previewText,
        voiceId
      );
      
      if (response.data.success) {
        setAudioUrl(response.data.audio_url);
      }
    } catch (err) {
      setError('Failed to generate preview');
    } finally {
      setGeneratingTTS(false);
    }
  };

  const togglePlayback = () => {
    if (audioRef.current) {
      if (playing) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Alerts */}
      {success && (
        <div className="fixed top-4 right-4 z-50 bg-green-500/20 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-400">{success}</span>
        </div>
      )}
      {error && (
        <div className="fixed top-4 right-4 z-50 bg-red-500/20 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Clone Voice</h1>
          <p className="text-gray-400">Create custom AI voices from audio samples</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={fetchVoices}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1f] border border-gray-700 rounded-lg text-gray-400 hover:text-white"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button 
            onClick={() => setShowCloneModal(true)}
            disabled={!apiConfigured}
            className="flex items-center gap-2 px-4 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-5 h-5" />
            Clone New Voice
          </button>
        </div>
      </div>

      {/* API Status Warning */}
      {!apiConfigured && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-amber-400" />
            <div>
              <h3 className="text-amber-400 font-semibold">Voice Cloning Not Available</h3>
              <p className="text-gray-400 text-sm">ElevenLabs API key is not configured. Contact your administrator to enable voice cloning.</p>
            </div>
          </div>
        </div>
      )}

      {/* Cloned Voices */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">Your Cloned Voices</h2>
        
        {voices.cloned.length === 0 ? (
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-12 text-center">
            <Mic className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg text-gray-400 mb-2">No Cloned Voices Yet</h3>
            <p className="text-gray-500 text-sm mb-4">Upload audio samples to create your first custom voice</p>
            <button 
              onClick={() => setShowCloneModal(true)}
              disabled={!apiConfigured}
              className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
            >
              Clone Your First Voice
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {voices.cloned.map((voice) => (
              <div key={voice.id} className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Mic className="w-6 h-6 text-white" />
                  </div>
                  <span className={`px-2 py-1 text-xs rounded ${
                    voice.status === 'ready' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {voice.status}
                  </span>
                </div>
                
                <h3 className="text-white font-semibold mb-1">{voice.name}</h3>
                <p className="text-gray-500 text-sm mb-4">{voice.description || 'Custom cloned voice'}</p>
                
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handlePreviewVoice(voice.voice_id)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#1a1a1f] border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:border-gray-600"
                  >
                    <Play className="w-4 h-4" />
                    Preview
                  </button>
                  <button 
                    onClick={() => handleDeleteVoice(voice.voice_id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg text-gray-500 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Available Voices */}
      {voices.available.length > 0 && (
        <section>
          <h2 className="text-xl font-semibold text-white mb-4">Available Voices</h2>
          <div className="grid grid-cols-4 gap-4">
            {voices.available.slice(0, 8).map((voice) => (
              <div key={voice.voice_id} className="bg-[#0d1117] border border-gray-800 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center">
                    <Volume2 className="w-5 h-5 text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">{voice.name}</h3>
                    <p className="text-gray-500 text-xs capitalize">{voice.category}</p>
                  </div>
                </div>
                <button 
                  onClick={() => handlePreviewVoice(voice.voice_id)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#1a1a1f] border border-gray-700 rounded-lg text-gray-300 hover:text-white text-sm"
                >
                  <Play className="w-4 h-4" />
                  Preview
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Clone Voice Modal */}
      {showCloneModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <Wand2 className="w-5 h-5 text-teal-400" />
                <h3 className="text-lg font-semibold text-white">Clone New Voice</h3>
              </div>
              <button onClick={() => setShowCloneModal(false)} className="p-2 hover:bg-gray-800 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Voice Name *</label>
                <input
                  type="text"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  placeholder="e.g., Sarah - Customer Support"
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">Description</label>
                <input
                  type="text"
                  value={cloneDescription}
                  onChange={(e) => setCloneDescription(e.target.value)}
                  placeholder="Optional description"
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-2">Audio Samples *</label>
                <p className="text-gray-500 text-xs mb-3">Upload 1-5 audio files (MP3, WAV, M4A). Total ~20 seconds recommended.</p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="audio/mpeg,audio/wav,audio/mp4,audio/x-m4a"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-700 rounded-xl p-6 text-center cursor-pointer hover:border-gray-600"
                >
                  <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">Click to upload audio files</p>
                </div>
                
                {audioFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {audioFiles.map((file, i) => (
                      <div key={i} className="flex items-center justify-between bg-[#1a1a1f] rounded-lg px-3 py-2">
                        <span className="text-white text-sm truncate">{file.name}</span>
                        <button onClick={() => removeFile(i)} className="text-gray-500 hover:text-red-400">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex gap-3 p-4 border-t border-gray-800">
              <button
                onClick={() => setShowCloneModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCloneVoice}
                disabled={cloning || !cloneName || audioFiles.length === 0}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
              >
                {cloning ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}
                {cloning ? 'Cloning...' : 'Clone Voice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TTS Preview Modal */}
      {previewVoiceId && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h3 className="text-lg font-semibold text-white">Voice Preview</h3>
              <button onClick={() => { setPreviewVoiceId(null); setAudioUrl(null); }} className="p-2 hover:bg-gray-800 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Preview Text</label>
                <textarea
                  value={previewText}
                  onChange={(e) => setPreviewText(e.target.value)}
                  rows={3}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400 resize-none"
                />
              </div>
              
              {audioUrl && (
                <div className="bg-[#1a1a1f] rounded-lg p-4">
                  <audio ref={audioRef} src={audioUrl} onEnded={() => setPlaying(false)} />
                  <div className="flex items-center gap-4">
                    <button onClick={togglePlayback} className="p-3 bg-teal-500 rounded-full text-white">
                      {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                    </button>
                    <div className="flex-1">
                      <p className="text-white text-sm">Generated Audio</p>
                      <p className="text-gray-500 text-xs">Click play to listen</p>
                    </div>
                  </div>
                </div>
              )}
              
              <button
                onClick={() => handlePreviewVoice(previewVoiceId)}
                disabled={generatingTTS || !previewText}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
              >
                {generatingTTS ? <Loader2 className="w-5 h-5 animate-spin" /> : <Volume2 className="w-5 h-5" />}
                {generatingTTS ? 'Generating...' : 'Generate Preview'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CloneVoicePage;
